/**
 * Shared QR scanner for field check-in tools. Encapsulates the Html5Qrcode
 * start/stop lifecycle, the secure-context (HTTPS) guard, and decode → onDecode.
 * Tool-agnostic primitive — used by Event Field Check-in; the gun-show booth could
 * adopt it later. Renders a "Scan QR code" button; while scanning, the camera view
 * + a Cancel button. Errors are surfaced via onError (the host shows its own banner).
 */
import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";

interface Props {
  elementId?: string;
  buttonLabel?: string;
  onDecode: (text: string) => void;
  onError?: (message: string) => void;
}

export default function QrScanner({
  elementId = "qr-reader",
  buttonLabel = "Scan QR code",
  onDecode,
  onError,
}: Props) {
  const [scanning, setScanning] = useState(false);
  const ref = useRef<Html5Qrcode | null>(null);

  useEffect(() => {
    return () => {
      if (ref.current) {
        ref.current.stop().catch(() => {});
      }
    };
  }, []);

  const stop = async () => {
    if (ref.current) {
      try {
        await ref.current.stop();
      } catch {
        /* already stopped */
      }
      ref.current = null;
    }
    setScanning(false);
  };

  const start = async () => {
    setScanning(true);
    const secure =
      window.isSecureContext ||
      window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1";
    if (!secure) {
      setScanning(false);
      onError?.("Camera needs HTTPS. Enter the code manually.");
      return;
    }
    try {
      await new Promise((r) => setTimeout(r, 100));
      const scanner = new Html5Qrcode(elementId);
      ref.current = scanner;
      await scanner.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 250, height: 250 } },
        (decoded) => {
          stop();
          onDecode(decoded);
        },
        () => {},
      );
    } catch {
      setScanning(false);
      onError?.("Could not start camera. Enter the code manually.");
    }
  };

  if (scanning) {
    return (
      <div>
        <div id={elementId} className="overflow-hidden rounded-lg" />
        <button
          type="button"
          onClick={stop}
          className="mt-2 w-full rounded-md border border-gray-300 bg-white py-2 text-sm font-medium text-gray-700"
        >
          Cancel scan
        </button>
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={start}
      className="w-full rounded-md bg-primary-600 py-3 text-sm font-semibold text-white"
    >
      {buttonLabel}
    </button>
  );
}
